import { Moment } from 'moment'

import { AppDateFormats } from 'Core/constants'
import { ConfigItem, DefaultFile } from 'Core/interfaces'
import { ConfigFilterItem } from 'Core/interfaces/filters'

export enum ServiceOccurrenceFilterFields {
	actualServiceDateFrom = 'actualServiceDateFrom',
	actualServiceDateTo = 'actualServiceDateTo',
	searchPeriod = 'searchPeriod',
	serviceOccurrenceStatusId = 'serviceOccurrenceStatusId',
	supplierServiceOccurrenceStatusId = 'supplierServiceOccurrenceStatusId'
}

export enum CompanyCustomerFilterSearchFields {
	searchByCompanyId = 'searchByCompanyId',
	searchByCompanyName = 'searchByCompanyName',
	searchByCustomerId = 'searchByCustomerId',
	searchByCustomerName = 'searchByCustomerName'
}

export enum TransporterSearchResultFields {
	searchByDotId = 'searchByDotId',
	searchByEpaId = 'searchByEpaId',
	searchById = 'searchById',
	searchByName = 'searchByName',
	searchBySupplierLocationId = 'searchBySupplierLocationId',
	searchBySupplierLocationName = 'searchBySupplierLocationName'
}

export enum ServiceSiteSearchResultFields {
	searchResultById = 'searchResultById',
	searchResultByName = 'searchResultByName'
}

export enum ManifestSearchResultFields {
	searchByManifestId = 'searchByManifestId',
	searchByManifestDocumentNumber = 'searchByManifestDocumentNumber',
	searchByServiceSiteDocumentNumber = 'searchByServiceSiteDocumentNumber',
	searchByServiceSiteId = 'searchByServiceSiteId',
	searchByServiceOccurrenceId = 'searchByServiceOccurrenceId'
}

export enum InitialDisposalFacilitySearchResultFields {
	searchById = 'searchById',
	searchByName = 'searchByName',
	searchByEpaId = 'searchByEpaId',
	searchBySupplierLocationId = 'searchBySupplierLocationId',
	searchBySupplierLocationName = 'searchBySupplierLocationName'
}

export interface ManifestItem {
	id: number
	pageNumber: number
	lineNumber: string
	lineNumberAndLetter?: string
}
export enum ServiceOccurrenceStatus {
	Canceled = 'Canceled',
	CompleteDataTrackingOnly = 'Complete - Data Tracking Only',
	Initiated = 'Initiated',
	Invoiced = 'Invoiced',
	Prebilled = 'Prebilled',
	ServiceComplete = 'Service Complete',
	WaitinOnBackup = 'Waiting on Backup'
}
export enum SupplierServiceOccurrenceStatus {
	Canceled = 'Canceled',
	CompleteDataTrackingOnly = 'Complete - Data Tracking Only',
	Initiated = 'Initiated',
	Invoiced = 'Invoiced',
	Prebilled = 'Prebilled',
	ServiceComplete = 'Service Complete',
	WaitingOnBackup = 'Waiting on Backup'
}

export interface ServiceOccurenceItem {
	chargeType: Entity
}

export enum MandatoryChargeType {
	disposal = 'disposal',
	recycling = 'recycling'
}

export enum CustomerOrderIdFields {
	customerOrderId = 'customerOrderId',
	customerOrderSearch = 'customerOrderSearch'
}

export enum SupplierInvoiceBarCodeFields {
	searchByInvoiceNumber = 'searchByInvoiceNumber',
	searchByBarcode = 'searchByBarcode',
	supplierInvoiceSearch = 'supplierInvoiceSearch'
}

export enum ServiceSiteSOFields {
	searchById = 'searchById',
	searchByName = 'searchByName',
	serviceSiteSearch = 'serviceSiteSearch'
}

export enum CustomerCompanyFields {
	customerCompanySearch = 'customerCompanySearch'
}

export declare namespace ServiceOccurrence {
	export interface EditTableConfig<
		SelectFieldNames extends string,
		InputFieldNames extends string,
		DatepickerFields extends string
	> {
		selects: Record<
			SelectFieldNames,
			{
				options: LabelValue<number>[]
			}
		>
		inputs: Record<InputFieldNames, {}>
		datepicker: Record<DatepickerFields, { getCurrentDateFormat: AppDateFormats }>
	}

	interface SearchPeriod {
		name: string
		to: string
		from: string
	}

	interface Basic {
		id: number
		uniqueId: number
		status: Entity<ServiceOccurrenceStatus>
		serviceOccurrenceSupplierId: BasicNumber
		serviceOccurrenceSupplierStatus: Entity<ServiceOccurrenceStatus> | null
		serviceSiteId: number
		actualServiceDate: string | null
		customerOrderId: number
		customerInvoiceId: number | null
		supplierInvoiceId: number
		supplierInvoiceNumber: string
		primaryQuantity: number | null
		barCode: string
	}

	interface Detailed {
		id: number
		customerOrderId: number
		expectedServiceDate: string | null
		actualServiceDate: string | null
		status: Entity
		serviceSiteId: BasicNumber
		primaryQuantity: number
		revenueRecognitionDate: string | null
		customerOrderType: Entity
		orderedBy: string
		modificationComments: string
		transaction: Transaction | null
	}

	interface BillingItemDetails {
		id: number
		status: Entity
		billingAgency: Entity
		billingFreqCode: Entity | null
		customer: Entity
		serviceSite: Entity
		item: Entity
		chargeTypeCode: Entity
		unitOfMeasure: Entity
		serviceOccurrence: Detailed
		contract: string
		chargeDescription: string
		totalCharges: number
		totalChargesCurrency: string
		billingQuantity: number
		modifiedBillingQuantity: number
		modifiedUnitPrice: number
		modifiedExtendedPrice: number
	}

	interface ScaleInformation {
		scaleId: number
		scalePriceId: number
		scaleName: BasicString
		scalePriceName: BasicString
	}

	interface Transaction {
		id: number
		status: Entity
		isActive: boolean
		transporter: Entity | null
		service: Entity
		manifest: Entity
		initialDisposalFacility: Entity
		finalDisposalFacility: Entity
		wastestream: Entity
		billOfLading: string
		transactionDocument: Entity
		transactionItemId: number
		supplierTransactionCode: string
		supplierTransactionCode2: string
		totalPounds: number
		totalKilograms: number
		manifestItem: ManifestItem
		item: ServiceOccurenceItem
		himexManifestQuantity: number
		himexManifestUnitOfMeasure: Entity
		serviceComments: string
		hasSupplierInvoiceItem: boolean
		containerQuantity: Key
		scaleInformation: ScaleInformation
	}
	namespace SupplierInformation {
		interface SupplierLocations {
			serviceOccurrenceSupplierId: number
			serviceOccurrenceSupplierStatus: Entity
			updateFailed: boolean
			updatedBy: string
			dateChanged: string
			notes: string | null
		}

		interface SupplierInvoices {
			supplierInvoiceNumber: string
			supplierInvoiceId: number | null
			invoiceStatus: Entity
			supplierLocation: Entity
			billingAgency: Entity
			totalCharges: number
			currency: IdCode
			accountingDate: string
			invoiceScanId: number
			scanRecordId: number | null
		}

		interface Generic {
			supplierLocations: SupplierLocations[]
			supplierInvoices: SupplierInvoices[]
		}
	}

	interface CustomerInvoice {
		customerInvoiceId: number
		invoiceStatus: Entity
		billingAgency: Entity
		customer: Entity
		totalCharges: number
		totalChargesCurrency: string
		accountingDate: string | null
	}
	namespace Search {
		namespace Result {
			interface DotId extends Entity {
				dotId: string
			}
			interface EpaId extends Entity {
				epaId: string
			}
			interface SupplierLocation extends Entity {
				supplierLocation: Entity
			}

			interface ManifestBasic {
				manifestId: string
				manifestDocumentNumber: string
			}

			interface ManifestServiceSite extends ManifestBasic {
				serviceSiteId: string
				serviceSiteDocumentNumber: string
			}

			interface ManifestServiceOccurrence extends ManifestBasic {
				serviceOccurrenceId: string
			}

			interface Transporter {
				[TransporterSearchResultFields.searchByDotId]: DotId[]
				[TransporterSearchResultFields.searchByEpaId]: EpaId[]
				[TransporterSearchResultFields.searchById]: Entity[]
				[TransporterSearchResultFields.searchByName]: Entity[]
				[TransporterSearchResultFields.searchBySupplierLocationId]: SupplierLocation[]
				[TransporterSearchResultFields.searchBySupplierLocationName]: SupplierLocation[]
			}
			interface ServiceSite {
				[ServiceSiteSearchResultFields.searchResultById]: Entity[]
				[ServiceSiteSearchResultFields.searchResultByName]: Entity[]
			}
			interface Manifest {
				[ManifestSearchResultFields.searchByManifestId]: ManifestBasic[]
				[ManifestSearchResultFields.searchByManifestDocumentNumber]: ManifestBasic[]
				[ManifestSearchResultFields.searchByServiceSiteDocumentNumber]: ManifestServiceSite[]
				[ManifestSearchResultFields.searchByServiceSiteId]: ManifestServiceSite[]
				[ManifestSearchResultFields.searchByServiceOccurrenceId]: ManifestServiceOccurrence[]
			}
			interface InitialDisposalFacility {
				[InitialDisposalFacilitySearchResultFields.searchById]: Entity[]
				[InitialDisposalFacilitySearchResultFields.searchByName]: Entity[]
				[InitialDisposalFacilitySearchResultFields.searchByEpaId]: EpaId[]
				[InitialDisposalFacilitySearchResultFields.searchBySupplierLocationId]: SupplierLocation[]
				[InitialDisposalFacilitySearchResultFields.searchBySupplierLocationName]: SupplierLocation[]
			}
		}
	}

	namespace Filter {
		interface DateFilterData {
			fromDate: BasicString
			toDate: BasicString
		}
		interface Config {
			[ServiceOccurrenceFilterFields.actualServiceDateFrom]: ConfigFilterItem<
				string,
				BasicString
			>
			[ServiceOccurrenceFilterFields.actualServiceDateTo]: ConfigFilterItem<string, null>
			[ServiceOccurrenceFilterFields.searchPeriod]: ConfigFilterItem<string, null>
		}
		interface Status {
			[ServiceOccurrenceFilterFields.serviceOccurrenceStatusId]: ConfigFilterItem<
				string,
				string
			>
			[ServiceOccurrenceFilterFields.supplierServiceOccurrenceStatusId]: ConfigFilterItem<
				string,
				string
			>
		}
		namespace Search {
			interface Config {
				[CompanyCustomerFilterSearchFields.searchByCompanyId]: ConfigItem<CompanyCustomerFilterSearchFields>
				[CompanyCustomerFilterSearchFields.searchByCompanyName]: ConfigItem<CompanyCustomerFilterSearchFields>
				[CompanyCustomerFilterSearchFields.searchByCustomerId]: ConfigItem<CompanyCustomerFilterSearchFields>
				[CompanyCustomerFilterSearchFields.searchByCustomerName]: ConfigItem<CompanyCustomerFilterSearchFields>
			}

			interface Result {
				[CompanyCustomerFilterSearchFields.searchByCompanyId]: string[]
				[CompanyCustomerFilterSearchFields.searchByCompanyName]: string[]
				[CompanyCustomerFilterSearchFields.searchByCustomerId]: string[]
				[CompanyCustomerFilterSearchFields.searchByCustomerName]: string[]
			}

			namespace SupplierInvoiceBarCode {
				interface Config {
					[SupplierInvoiceBarCodeFields.searchByBarcode]: ConfigItem<SupplierInvoiceBarCodeFields>
					[SupplierInvoiceBarCodeFields.searchByInvoiceNumber]: ConfigItem<SupplierInvoiceBarCodeFields>
				}

				interface Result {
					[SupplierInvoiceBarCodeFields.searchByBarcode]: string[]
					[SupplierInvoiceBarCodeFields.searchByInvoiceNumber]: string[]
				}
			}
			namespace ServiceSite {
				interface Config {
					[ServiceSiteSOFields.searchById]: ConfigItem<ServiceSiteSOFields>
					[ServiceSiteSOFields.searchByName]: ConfigItem<ServiceSiteSOFields>
				}

				interface Result {
					[ServiceSiteSOFields.searchById]: string[]
					[ServiceSiteSOFields.searchByName]: string[]
				}
			}
		}
	}

	namespace Form {
		interface Edit {
			customerOrderId: Key | null
			expectedServiceDate: Moment | null
			actualServiceDate: Moment | null
			status: string | null
			primaryQuantity: Key | null
			revenueRecognitionDate: string | null
			customerOrderType: string | null
			orderedBy: string | null
			modificationComments: string | null
			transaction: {
				id?: Key | null
				status?: Key | null
				billOfLandingNumber: Key | null
				transporterId: LabelValue<number> | null
				serviceSiteId: LabelValue<number> | null
				manifestId: LabelValue<number> | null
				initialDisposalFacilityId: LabelValue<number> | null
				finalDisposalFacilityId: LabelValue<number> | null
				wastestreamId?: LabelValueExtra<number> | null
				transactionItemId?: Key | null
				item?: Key | null
				manifestItemId: LabelValue<number> | null
				supplierTransactionCode?: Key | null
				supplierTransactionCode2?: Key | null
				quantity?: Key | null
				totalPounds?: Key | null
				totalKgs?: Key | null
				isActive: boolean
				himexManifestQuantity?: Key | null
				himexManifestUnitOfMeasureId: Key | null
				serviceComments?: Key | null
				scaleInformation?: BasicString
			}
		}
	}

	namespace Request {
		interface Edit {
			expectedServiceDate: Key | null
			actualServiceDate: Key | null
			primaryQuantity: Key | null
			modificationComments: Key | null
			statusId: number
			transaction: {
				billOfLandingNumber: Key | null
				transporterId: Key | null
				serviceSiteId: number | null
				manifestId: Key | null
				initialDisposalFacilityId: Key | null
				finalDisposalFacilityId: Key | null
				wastestreamId: Key | null
			} | null
			transactionItem: {
				manifestItemId: Key | null
				supplierTransactionCode: Key | null
				supplierTransactionCode2: Key | null
				quantity: Key | null
				totalPounds: Key | null
				totalKgs: Key | null
				isActive: boolean
				himexManifestQuantity: Key | null
				himexManifestUnitOfMeasureId: Key | null
				serviceComments: Key | null
			} | null
		}
	}
	namespace TransactionDocument {
		namespace Form {
			interface Add {
				documentTypeId: Key | null
				file: DefaultFile | null
			}
		}
		namespace Request {
			interface Add {
				documentTypeId: Key | null
				file: File | null
			}
		}
	}
	namespace ServiceConfirmation {
		namespace Form {
			interface Add {
				itemId: LabelValue | null
			}
		}
	}

	namespace Status {
		namespace Request {
			interface Edit {
				ids: Key[]
				statusId?: Key
			}
		}
	}

	namespace MassEdit {
		namespace Form {
			interface Edit {
				status: LabelValue<number> | null
				serviceOccurrenceSupplierStatus: LabelValue<number> | null
				primaryQuantity: number | null
				actualServiceDate: moment.Moment | null
			}
			interface Approve {
				actualServiceDate: moment.Moment | null
			}
			interface Cancel {
				makeBilingItemNonBillable: boolean
			}
		}
		namespace Request {
			interface Edit {
				statusId: Key | null
				serviceOccurrenceSupplierStatusId: number | null
				serviceOccurrenceSupplierId: number | null
				primaryQuantity: number | null
				actualServiceDate: string | null
				makeBilingItemNonBillable: boolean
			}
		}
		namespace Response {
			interface Edit {
				lastUpdateDate: string
				lastUpdatedBy: string
				serviceOccurrenceSupplierId: number
			}
		}
	}

	namespace Validate {
		interface Basic {
			supplierServiceOccurrenceCount: number
			notNonBillableBillingItems: number[]
			invoicedBillingItems: number[]
		}

		interface Form {
			makeBillingItemNonBillable: boolean
		}

		interface Request {
			serviceOccurrenceId: BasicNumber
			supplierServiceOccurrenceId: BasicNumber
		}
	}
}
